<template>
  <ShareLinkCard
    :name="content.deceased.name"
    :page-url="pageUrl"
    :title="forFilm('linkTitle', 'lifeShareTitle')"
  >
    <!-- Computers share the link: it opens the memorial page with the slideshow playing.
         The slideshow behind the card is the preview; the film file is a quiet extra. -->
    <template v-if="exporter.videoMode.value">
      <!-- Stories live on the phone: the QR code opens this sharing view there. -->
      <div class="slideshow-share-card__phone">
        <div class="slideshow-share-card__qr" v-if="qr" v-html="qr" />
        <div>
          <b>{{ texts.storyCardTitle }}</b>
          <p>{{ texts.storyCardScan }}</p>
        </div>
      </div>

      <div class="slideshow-share-card__file">
        <p class="slideshow-share-card__done" v-if="downloaded">
          <SlideshowIcon name="check" />
          {{ texts.savedDownloads }}
        </p>
        <p
          aria-live="polite"
          class="slideshow-share-card__making"
          v-else-if="exporter.making.value"
        >
          <span>{{ texts.preparingFilm }} {{ exporter.percent.value }} %</span>
          <button
            class="slideshow-share-card__textlink"
            type="button"
            @click="exporter.cancel"
          >
            {{ texts.cancel }}
          </button>
        </p>
        <button
          class="slideshow-share-card__download"
          type="button"
          v-else
          @click="download"
        >
          {{ texts.downloadFilm }}
        </button>
        <p
          aria-live="polite"
          class="slideshow-share-card__note"
          v-if="exporter.notice.value"
        >
          {{ exporter.notice.value }}
        </p>
      </div>
    </template>
  </ShareLinkCard>
</template>

<script lang="ts" setup>
  import { onMounted, ref } from "vue";
  import {
    type FilmExport,
    useFilmExport,
  } from "../../composables/useFilmExport";
  import {
    forFilm,
    slideshowData,
    slideshowTexts,
  } from "../../composables/useSlideshow";
  import type { PageContent } from "../../slideshow/playlist";
  import {
    qrAddress,
    qrSvg,
    download as saveFile,
  } from "../../slideshow/share";
  import { track } from "../../slideshow/track";
  import ShareLinkCard from "./ShareLinkCard.vue";
  import SlideshowIcon from "./SlideshowIcon.vue";

  const props = defineProps<{
    content: PageContent;
    /** The viewer's film, so the end card's download and this one make it once. */
    exporter?: FilmExport;
    /** The memorial page, opening on the slideshow (…#bildspel). */
    pageUrl: string;
    name: string;
    sectionId: string;
  }>();

  const texts = slideshowTexts();
  const qr = ref("");
  const downloaded = ref(false);
  const exporter =
    props.exporter ??
    useFilmExport(
      () => props.content,
      () => props.name,
      false
    );

  /** The film as a file, made only when asked for (computers share the link first). */
  async function download() {
    const file = await exporter.ensureFile("landscape");
    if (file) {
      saveFile(file);
      downloaded.value = true;
      track({
        action: "video_downloaded",
        format: "landscape",
        surface: "share_card",
      });
    }
  }

  onMounted(() => {
    track({ action: "share_open", format: "landscape" });
    if (exporter.videoMode.value) {
      qrSvg(
        qrAddress(
          props.pageUrl,
          slideshowData()?.pageId ?? "",
          `${props.sectionId}-dela`
        )
      )
        .then((svg) => {
          qr.value = svg;
        })
        .catch(() => undefined);
    }
  });
</script>
